import { fetchUrl } from '@/services/ppApi';

class PrefetchManager {
    constructor() {
        this.queue = [];
        this.isPrefetching = false;
        this.currentController = null;
        this.delayTimer = null;
        this.cache = new Set(); // Track already prefetched URLs
        this.maxConcurrent = 3; // Number of concurrent prefetch requests
        this.activePrefetches = 0;
        this.retryCount = new Map(); // Track retry attempts
        this.maxRetries = 2; // Maximum retry attempts
        this.activeControllers = new Map(); // Track all active abort controllers
        this.maxCacheSize = 100; // Limit cache size
    }

    /**
     * Enqueue URLs for prefetching with priority support
     * @param {Array<string>} urls 
     * @param {number} delay - Optional delay in milliseconds
     * @param {number} priority - Priority level (lower number = higher priority)
     */
    enqueue(urls, delay = 0, priority = 10) {
        console.log('Enqueue: ' + Date.now().toString());
        console.log('Max. conc.: ' + this.maxConcurrent)
        // Filter out URLs that have already been prefetched
        const newUrls = urls.filter(url => !this.cache.has(url))
            .map(url => ({ url, priority }));
        
        // Add new URLs to the queue
        this.queue.push(...newUrls);
        
        // Clear any existing delay timer
        if (this.delayTimer) {
            clearTimeout(this.delayTimer);
            this.delayTimer = null;
        }
        
        if (delay > 0) {
            this.delayTimer = setTimeout(() => {
                this.delayTimer = null;
                this.processQueue();
            }, delay);
        } else {
            this.processQueue();
        }
    }

    /**
     * Clears the prefetch queue and aborts any ongoing prefetch
     */
    clearQueue() {
        this.queue = [];
        
        // Clear any pending delay timer
        if (this.delayTimer) {
            clearTimeout(this.delayTimer);
            this.delayTimer = null;
        }
        
        // Abort all active controllers
        this.activeControllers.forEach(controller => {
            controller.abort();
        });
        this.activeControllers.clear();
        
        this.isPrefetching = false;

        // Reset active prefetches count
        this.activePrefetches = 0;
    }

    /**
     * Process the queue with priority sorting and concurrent fetching
     */
    async processQueue() {
        if (this.queue.length === 0) return;
        
        // Check for active fetches in the application
        if (window.activeFetches && window.activeFetches > 1) {
            setTimeout(() => this.processQueue(), 200);
            return;
        }
        
        // Sort queue by priority (lower numbers first)
        this.queue.sort((a, b) => a.priority - b.priority);
        
        // Process multiple items concurrently up to maxConcurrent
        while (this.queue.length > 0 && this.activePrefetches < this.maxConcurrent) {
            this.activePrefetches++;
            const item = this.queue.shift();
            this.prefetchUrl(item.url);
        }
    }
    
    /**
     * Prefetch a single URL with retry capabilities
     */
    async prefetchUrl(url) {
        const controller = new AbortController();
        const id = Date.now() + Math.random().toString(36).substr(2, 9);
        this.activeControllers.set(id, controller);
        
        try {
            // Add to cache after successful fetch instead of before
            await fetchUrl(url, controller.signal);
            console.log('Fetch: ' + Date.now().toString());
            console.log('Prefetching: ' + url);
            this.cache.add(url);
        } catch (error) {
            // Handle retry logic
            const attempts = this.retryCount.get(url) || 0;
            if (attempts < this.maxRetries && !controller.signal.aborted) {
                this.retryCount.set(url, attempts + 1);
                this.queue.unshift({ url, priority: 5 }); // Retry with higher priority
            }
        } finally {
            this.activePrefetches--;
            this.activeControllers.delete(id);
            
            // Limit cache size by removing oldest entries if needed
            if (this.cache.size > this.maxCacheSize) {
                const entriesToRemove = Array.from(this.cache).slice(0, Math.floor(this.maxCacheSize * 0.2));
                entriesToRemove.forEach(entry => this.cache.delete(entry));
            }
            
            this.processQueue(); // Continue with next URLs
        }
    }
    
    /**
     * Conditionally adjust prefetching based on network conditions
     */
    adjustForNetworkConditions() {
        // Check if the browser supports the Network Information API
        if (navigator.connection) {
            const connection = navigator.connection;
            
            // Adjust concurrent requests based on network type
            if (connection.effectiveType === '4g') {
                this.maxConcurrent = 4;
            } else if (connection.effectiveType === '3g') {
                this.maxConcurrent = 3;
            } else {
                this.maxConcurrent = 1;
            }
            
            // Add event listener for connection changes
            connection.addEventListener('change', () => {
                this.adjustForNetworkConditions();
            });
        }
    }
    
    /**
     * Clear the cache of prefetched URLs (useful for long-running sessions)
     */
    clearCache() {
        this.cache.clear();
    }
}

export const prefetchManager = new PrefetchManager();
// Initialize network condition adjustment
prefetchManager.adjustForNetworkConditions();
